import React from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import IamSessionControlPolicyForm from '@devnw/cyber-iam/iam/SessionControlPolicyForm/SessionControlPolicyForm'
import type { SessionControlPolicy } from '@devnw/cyber-iam/iam/types'
import type { AdminSessionControlPolicyResp } from '@gen/react/client'
import { useAdminGenClient } from '../../api/genClient'
import { useActiveTenant } from '../../layout/TenantSwitcher'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const DEFAULT_POLICY: SessionControlPolicy = {
  idleTtlSeconds: 3600,
  absTtlSeconds: 28800,
  maxConcurrentPerSubject: 1,
  allowPause: true,
}

// AdminSessionControlPolicyResp is already camelCase (fromWire() converts the
// wire's idle_ttl_seconds/abs_ttl_seconds/max_concurrent_per_subject/
// allow_pause) and its field names already match SessionControlPolicy 1:1 —
// unlike EmergencyRole/BreakGlassEvent (see ../../api/breakGlassMapping.ts),
// this is a typed (non-opaque) response, so this is a straight
// optional->required fill, not a case remap. The pre-migration page mapped
// this camelCase response into its OWN snake_case PolicyForm state
// (idle_ttl_seconds, ...) purely for its MUI TextFields; that indirection is
// no longer needed now that SessionControlPolicy is camelCase throughout.
function toIamPolicy(resp: AdminSessionControlPolicyResp): SessionControlPolicy {
  return {
    idleTtlSeconds: resp.idleTtlSeconds ?? DEFAULT_POLICY.idleTtlSeconds,
    absTtlSeconds: resp.absTtlSeconds ?? DEFAULT_POLICY.absTtlSeconds,
    maxConcurrentPerSubject:
      resp.maxConcurrentPerSubject ?? DEFAULT_POLICY.maxConcurrentPerSubject,
    allowPause: resp.allowPause ?? DEFAULT_POLICY.allowPause,
  }
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

/**
 * OIDCPAM Gate-6 Phase 5b: migrated off MUI onto
 * @devnw/cyber-iam/iam/SessionControlPolicyForm — the exact parity component
 * built from this page. The component owns load/edit/save/delete plus
 * per-action busy state; this page still owns the three API calls and the
 * not-found -> null mapping onLoad requires (the library never sniffs error
 * strings itself).
 *
 * `key={tenant}` forces the form to remount (clearing its internal
 * resourceId/form/loadStatus) on a tenant switch — the pre-migration page did
 * this itself via a `useEffect` keyed on `tenant`; the library component has
 * no tenant awareness, so a host-driven remount is the equivalent here.
 */
export const SessionControlPolicyPage = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const handleLoad = async (
    resourceId: string
  ): Promise<SessionControlPolicy | null> => {
    try {
      const resp = await client.adminGetSessionControlPolicy({
        tenantId: tenant,
        resourceId,
      })
      return toIamPolicy(resp)
    } catch (e: unknown) {
      const msg = errMessage(e)
      if (msg.toLowerCase().includes('not found') || msg.includes('404')) {
        return null
      }
      throw e
    }
  }

  const handleSave = async (
    resourceId: string,
    policy: SessionControlPolicy
  ): Promise<void> => {
    await client.adminSetSessionControlPolicy({
      tenantId: tenant,
      resourceId,
      idleTtlSeconds: policy.idleTtlSeconds,
      absTtlSeconds: policy.absTtlSeconds,
      maxConcurrentPerSubject: policy.maxConcurrentPerSubject,
      allowPause: policy.allowPause,
    })
  }

  const handleDelete = async (resourceId: string): Promise<void> => {
    await client.adminDeleteSessionControlPolicy({ tenantId: tenant, resourceId })
  }

  if (tenant === 'system') {
    return (
      <PageContainer>
        <MutedText>
          Please select a tenant to manage session control policies.
        </MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <IamSessionControlPolicyForm
        key={tenant}
        testID="session-control-policy"
        title="Session Control Policy"
        onLoad={handleLoad}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </PageContainer>
  )
}
